import React, { useCallback } from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useMessageInstanceEvent } from '@atlassian/message-instance-event-frontend-client/useMessageInstanceEvent';

import { ConnectorSources } from '../../../constants/schemas/connector-sources';
import { OAuthSource } from '../../events/oauth-events';

import type {
	BannerVariant,
	FullConnectorVariant,
	QuickFindHeadlessAvailableConnector,
	QuickFindHeadlessAvailableConnectors,
	QuickFindHeadlessMessage,
	QuickFindHeadlessRendererProps,
	SmartLinkVariant,
} from './headless-types';

const SNOOZE_DAYS = [1, 1, 1, 90] as const;
const SHORT_SNOOZE_MINUTES = [1, 2, 3, 4] as const;

const messages = defineMessages({
	connect: {
		id: 'rovo.search.quick-find-3p.connect',
		defaultMessage: 'Connect {connectorName}',
		description: 'Button text to connect a third-party app',
	},
	dismiss: {
		id: 'rovo.search.quick-find-3p.dismiss',
		defaultMessage: 'Dismiss',
		description: 'Button text to dismiss the banner',
	},
	authorizationFail: {
		id: 'rovo.search.quick-find-authorization-fail',
		defaultMessage: 'Failed to connect to {product}.',
		description: 'Message displayed on flag when authorization fails.',
	},
	authorizationSuccess: {
		id: 'rovo.search.quick-find.authorization-success',
		defaultMessage: 'Account connected to {product}.',
		description: 'Message displayed on flag when authorization is successful.',
	},
	fullConnectorAdminBlessedMissingResultsHeading: {
		id: 'rovo.search.quick-find-3p.full-connector.admin-blessed-missing-results.heading-v2',
		defaultMessage: 'Your admin added {connectorName}. ',
		description: 'Heading for admin-blessed-missing-results full connector variant',
	},
	fullConnectorAdminBlessedMissingResultsBody: {
		id: 'rovo.search.quick-find-3p.full-connector.admin-blessed-missing-results.body',
		defaultMessage: 'Connect to see missing results',
		description: 'Body text for admin-blessed-missing-results full connector variant',
	},
	fullConnectorSocialProofHighHeading: {
		id: 'rovo.search.quick-find-3p.full-connector.social-proof-high.heading-v2',
		defaultMessage: '{teamConnectAppPercentage}% of your team have connected apps',
		description: 'Heading for social-proof-high full connector variant',
	},
	smartLinkSocialProofLowHeading: {
		id: 'rovo.search.quick-find-3p.smart-link-connector.social-proof-low.heading-v2',
		defaultMessage: '{connectorName} is now available!',
		description: 'Heading for social-proof-low smart link connector variant',
	},
	smartLinkSocialProofLowBody: {
		id: 'rovo.search.quick-find-3p.smart-link-connector.social-proof-low.body',
		defaultMessage: 'Connect your account to see missing results',
		description: 'Body text for social-proof-low smart link connector variant',
	},
	smartLinkSocialProofHighHeading: {
		id: 'rovo.search.quick-find-3p.smart-link-connector.social-proof-high.heading-v3',
		defaultMessage: '{teamConnectAppPercentage}% of your team have connected apps',
		description: 'Heading for social-proof-high smart link connector variant',
	},
});

const variantMessages: {
	'full-connector': Record<
		FullConnectorVariant,
		{ heading: MessageDescriptor; body?: MessageDescriptor }
	>;
	smartlinks: Record<SmartLinkVariant, { heading: MessageDescriptor; body?: MessageDescriptor }>;
} = {
	'full-connector': {
		'admin-blessed-missing-results': {
			heading: messages.fullConnectorAdminBlessedMissingResultsHeading,
			body: messages.fullConnectorAdminBlessedMissingResultsBody,
		},
		'social-proof-high': {
			heading: messages.fullConnectorSocialProofHighHeading,
		},
	},
	smartlinks: {
		'social-proof-low': {
			heading: messages.smartLinkSocialProofLowHeading,
			body: messages.smartLinkSocialProofLowBody,
		},
		'social-proof-high': {
			heading: messages.smartLinkSocialProofHighHeading,
		},
	},
};

type QuickFindHeadlessVariantMessages = { heading: MessageDescriptor; body?: MessageDescriptor };

const styles = cssMap({
	container: {
		paddingBottom: token('space.075'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
	},
	banner: {
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
		borderRadius: token('radius.large'),
	},
});

const selectConnectorForBanner = (
	availableConnectors: QuickFindHeadlessAvailableConnectors,
	lastDismissed3P: string,
): QuickFindHeadlessAvailableConnector | null => {
	if (!lastDismissed3P) {
		return availableConnectors.find((c) => c.userNeedsOAuth) ?? null;
	}

	const lastIndex = availableConnectors.findIndex((c) => c.connectorId === lastDismissed3P);
	// If Post Office references a connector that is no longer configured, restart the rotation.
	const candidates =
		lastIndex >= 0 ? availableConnectors.slice(lastIndex + 1) : availableConnectors;
	return candidates.find((c) => c.userNeedsOAuth) ?? null;
};

const getVariantMessages = (
	mode: 'smartlinks' | 'full-connector',
	variant: BannerVariant,
): QuickFindHeadlessVariantMessages | undefined => {
	if (mode === ConnectorSources.Smartlinks) {
		return variant in variantMessages.smartlinks
			? variantMessages.smartlinks[variant as SmartLinkVariant]
			: undefined;
	}
	return variant in variantMessages['full-connector']
		? variantMessages['full-connector'][variant as FullConnectorVariant]
		: undefined;
};

const QuickFindHeadlessBanner = ({
	messageInstanceId,
	connector,
	connectorMode,
	variant,
	variantMsgs,
	teamConnectAppPercentage,
	onAuthorize,
	onDismiss,
}: {
	messageInstanceId: string;
	connector: QuickFindHeadlessAvailableConnector;
	connectorMode: 'smartlinks' | 'full-connector';
	variant: BannerVariant;
	variantMsgs: QuickFindHeadlessVariantMessages;
	teamConnectAppPercentage?: number;
	onAuthorize: (oAuthSource: OAuthSource) => void;
	onDismiss: () => void;
}): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const connectorNameToUse =
		connector.connectorProductKey === 'sharepoint' ? 'SharePoint' : connector.connectorName;

	const headingText = formatMessage(variantMsgs.heading, {
		connectorName: connectorNameToUse,
		teamConnectAppPercentage,
	});
	const bodyText = variantMsgs.body
		? formatMessage(variantMsgs.body, {
				connectorName: connectorNameToUse,
				teamConnectAppPercentage,
			})
		: undefined;
	const connectText = formatMessage(messages.connect, { connectorName: connectorNameToUse });
	const dismissLabel = formatMessage(messages.dismiss);

	const ConnectorIcon = connector.icon;
	const headingId = `rovo-search-page-quick-find-3p-${messageInstanceId}-${connector.connectorId}-${connectorMode}-${variant}-heading`;

	return (
		<Box xcss={styles.container}>
			<Box
				aria-labelledby={headingId}
				backgroundColor="color.background.accent.blue.subtlest"
				xcss={styles.banner}
				testId="rovo-search-page-quick-find-3p-connector-pre-auth-nudge"
			>
				<Inline alignBlock="center" spread="space-between">
					<Inline alignBlock="center" space="space.100">
						{ConnectorIcon ? <ConnectorIcon size="small" label={connectorNameToUse} /> : null}
						<Text id={headingId}>
							<Text weight="semibold">{headingText}</Text>
							{bodyText ? ` ${bodyText}` : null}
						</Text>
					</Inline>
					<Inline alignBlock="center" space="space.100">
						<Button
							appearance="primary"
							onClick={() => onAuthorize(OAuthSource.quickFindOAuthBanner)}
							testId="rovo-search-page-quick-find-3p-connect"
						>
							{connectText}
						</Button>
						<IconButton
							appearance="subtle"
							icon={CrossIcon}
							label={dismissLabel}
							onClick={onDismiss}
							testId="rovo-search-page-quick-find-3p-dismiss"
						/>
					</Inline>
				</Inline>
			</Box>
		</Box>
	);
};

const QuickFindHeadlessMessage = ({
	messageInstanceId,
	messageContext,
	connector,
	connectorMode,
	variant,
	variantMsgs,
	useOAuthHandlers,
	dismiss,
}: {
	messageInstanceId: string;
	messageContext: Omit<QuickFindHeadlessMessage['context'], 'lastDismissed3P'>;
	connector: QuickFindHeadlessAvailableConnector;
	connectorMode: 'smartlinks' | 'full-connector';
	variant: BannerVariant;
	variantMsgs: QuickFindHeadlessVariantMessages;
	useOAuthHandlers: QuickFindHeadlessRendererProps['useOAuthHandlers'];
	dismiss: () => void;
}): React.JSX.Element | null => {
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { formatMessage } = useIntl();
	const { productSource, dismissalCount, teamConnectAppPercentage } = messageContext;
	const { connectorId } = connector;

	const { onAuthorize } = useOAuthHandlers({
		product: connector.connectorProductKey,
		connectorSource: connector.connectorMode,
		successFlagTitle: formatMessage(messages.authorizationSuccess, {
			product: connector.connectorName,
		}),
		failFlagTitle: formatMessage(messages.authorizationFail, {
			product: connector.connectorName,
		}),
		outboundAuthUrl: connector.oAuthUrl,
		isOnSearchDialog: true,
		extraAttributes: {
			connectorId,
			variant,
		},
	});

	const handleDismiss = useCallback(() => {
		void (async () => {
			const useShortSnooze = fg('po_enable_rovo-quick-find-3p-short-snooze-test');
			const snoozeDuration = useShortSnooze
				? (SHORT_SNOOZE_MINUTES[dismissalCount] ?? SHORT_SNOOZE_MINUTES[0])
				: (SNOOZE_DAYS[dismissalCount] ?? SNOOZE_DAYS[0]);

			try {
				await messageInstanceClicked({
					messageEventType: 'clicked',
					clickedElement: {
						clickIdentifier: 'dismiss-3p-banner',
						clickedElement: 'button',
						buttonType: 'secondary',
					},
					action: {
						actionType: 'snooze',
						snoozeDuration,
					},
					additionalMetadataFromFrontend: {
						actionReason: 'dismiss',
						dismissedConnectorId: connector.connectorId,
						dismissedVariant: variant,
						variant,
						productSource,
						connectorSource: connector.connectorMode,
						...(useShortSnooze ? { snoozeUnit: 'minutes' as const } : {}),
					},
				});
			} catch {
				// Post Office click tracking is best-effort; add Search analytics here only if we
				// decide this failure needs product-level observability.
			} finally {
				dismiss();
			}
		})();
	}, [messageInstanceClicked, dismiss, dismissalCount, connector, productSource, variant]);

	return (
		<QuickFindHeadlessBanner
			messageInstanceId={messageInstanceId}
			connector={connector}
			connectorMode={connectorMode}
			variant={variant}
			variantMsgs={variantMsgs}
			teamConnectAppPercentage={teamConnectAppPercentage}
			onAuthorize={onAuthorize}
			onDismiss={handleDismiss}
		/>
	);
};

export const QuickFindHeadlessMessageRenderer = ({
	message,
	availableConnectors,
	useOAuthHandlers,
	dismissMessage,
	PostOfficeMessageInstance,
}: QuickFindHeadlessRendererProps): React.JSX.Element | null => {
	const {
		productSource,
		lastDismissed3P,
		dismissalCount,
		fullConnectorVariant,
		smartLinkVariant,
		teamConnectAppPercentage,
	} = message.context;
	const connector = selectConnectorForBanner(availableConnectors, lastDismissed3P);
	const dismiss = useCallback(
		() => dismissMessage(message.messageInstanceId),
		[dismissMessage, message.messageInstanceId],
	);

	if (!connector) {
		return null;
	}

	const variant =
		connector.connectorMode === ConnectorSources.Smartlinks
			? smartLinkVariant
			: fullConnectorVariant;
	const connectorModeToUse =
		connector.connectorMode === ConnectorSources.Smartlinks
			? ConnectorSources.Smartlinks
			: ConnectorSources.Full;
	const variantMsgs = getVariantMessages(connectorModeToUse, variant);

	if (!variantMsgs) {
		return null;
	}

	return (
		<PostOfficeMessageInstance messageInstanceId={message.messageInstanceId}>
			{() => (
				<QuickFindHeadlessMessage
					messageInstanceId={message.messageInstanceId}
					messageContext={{
						productSource,
						dismissalCount,
						fullConnectorVariant,
						smartLinkVariant,
						teamConnectAppPercentage,
					}}
					connector={connector}
					connectorMode={connectorModeToUse}
					variant={variant}
					variantMsgs={variantMsgs}
					useOAuthHandlers={useOAuthHandlers}
					dismiss={dismiss}
				/>
			)}
		</PostOfficeMessageInstance>
	);
};
